import type {
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig,
} from "axios";
import { toast } from "react-hot-toast";
import { ERROR_MESSAGES } from "@/data/constants";
import type { ApiResponse, ApiError } from "@/types";
import { handleValidationError } from "../utils/validationErrorHandler";
import { getContextualErrorMessage } from "../utils/errorMessageHandler";
import { tokenRefreshService } from "../utils/tokenRefreshService";
import { httpClient } from "../httpClient";
import { markToastShown } from "@/utils/errorToast";

// Extend the Axios request config to include our retry flag and silent error flag
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _silentError?: boolean;
}

/**
 * Force logout when token refresh fails permanently.
 * Clears auth storage and redirects to login page.
 * Uses a flag to prevent multiple simultaneous logouts.
 */
let isForceLoggingOut = false;
const forceLogout = (message: string) => {
  if (isForceLoggingOut) return;
  isForceLoggingOut = true;

  // Clear auth from localStorage — Zustand will rehydrate as unauthenticated on page load
  localStorage.removeItem("auth-storage");
  toast.error(message);

  // Redirect to login if not already there
  if (
    !window.location.pathname.includes("/login") &&
    window.location.pathname !== "/"
  ) {
    window.location.href = "/login";
  }

  // Reset flag after redirect starts (allow future logouts if user logs in again)
  setTimeout(() => {
    isForceLoggingOut = false;
  }, 2000);
};

export const responseInterceptor = [
  (response: AxiosResponse<ApiResponse<unknown>, unknown>) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;
    const isSilent = originalRequest?._silentError === true;

    /** Show a toast and mark the error so components don't show a second one */
    const showToast = (message: string) => {
      if (!isSilent) {
        toast.error(message);
        markToastShown(error);
      }
    };

    if (error.response) {
      const { status, data } = error.response;
      const errorData = data as ApiError;
      const context = {
        endpoint: error.config?.url,
        method: error.config?.method,
        data: errorData,
      };

      // Endpoints where 401 is a validation error, not a session auth error
      // These should NOT trigger token refresh or logout
      const PASSWORD_VALIDATION_ENDPOINTS = [
        '/auth/change-password',
        'change-password',
      ];

      const isPasswordValidationError = PASSWORD_VALIDATION_ENDPOINTS.some(
        endpoint => originalRequest?.url?.includes(endpoint)
      );

      // Handle 401 errors with automatic token refresh
      // Skip for password validation errors - let component handle those
      if (
        status === 401 &&
        originalRequest &&
        !originalRequest.url?.includes("/auth/refresh-token") &&
        !isPasswordValidationError
      ) {

        // Check if this is an AI provider authentication error (not a user auth error)
        const isAIProviderError =
          errorData?.message?.toLowerCase().includes("api key") ||
          errorData?.message?.toLowerCase().includes("ai provider") ||
          originalRequest.url?.includes("/ai-agents/") ||
          originalRequest.url?.includes("/ai-providers/");

        if (isAIProviderError) {
          // This is an AI provider auth error, not a user auth error
          // Just show the error message, don't redirect to login
          showToast(errorData?.message || "AI provider authentication failed. Please check your API key configuration.");
          return Promise.reject(error);
        }

        // This is a user authentication error, try to refresh token
        try {
          const refreshToken = tokenRefreshService.getStoredRefreshToken();

          if (refreshToken && !originalRequest._retry) {
            originalRequest._retry = true;

            const newAccessToken = await tokenRefreshService.refreshToken(
              refreshToken
            );

            // Update the original request with the new token and ensure X-Team-Context is set
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

              // Ensure X-Team-Context header is included for the retry
              try {
                const authStorage = localStorage.getItem('auth-storage');
                if (authStorage) {
                  const parsedAuth = JSON.parse(authStorage);
                  const currentTeamId = parsedAuth.state?.currentTeamId;
                  if (currentTeamId) {
                    // Check if this endpoint needs team context
                    const TEAM_CONTEXT_EXCLUDED = [
                      '/auth/login', '/auth/register', '/auth/refresh-token',
                      '/auth/forgot-password', '/auth/reset-password',
                      '/auth/accept-invitation', '/auth/teams', '/auth/me/teams',
                      '/auth/set-team-context', '/auth/validate-team-context'
                    ];
                    const shouldInclude = !TEAM_CONTEXT_EXCLUDED.some(
                      endpoint => originalRequest.url?.includes(endpoint)
                    );
                    if (shouldInclude) {
                      originalRequest.headers['X-Team-Context'] = currentTeamId;
                    }
                  }
                }
              } catch {
                // Ignore parse errors - request will proceed without team context
              }
            }

            // Retry the original request
            return httpClient(originalRequest);
          }
        } catch {
          // Refresh token is invalid/expired — this is a permanent auth failure.
          // Force logout to prevent user getting stuck on authenticated pages.
          forceLogout("Session expired. Please login again.");
          return Promise.reject(error);
        }

        // No refresh token or already retried — force logout
        forceLogout("Session expired. Please login again.");
      } else if (status === 401 && isPasswordValidationError) {
        // Password validation 401 - don't show toast, let the component handle the error
        // Just fall through to Promise.reject(error) at the end
      } else {
        switch (status) {
          case 400:
            // For bad request errors, prefer the API message as it usually contains specific context
            // (e.g., "Cannot remove the last member from the team")
            showToast(errorData?.message || ERROR_MESSAGES.BAD_REQUEST);
            break;
          case 403:
            if (errorData?.message?.includes("account locked")) {
              showToast(ERROR_MESSAGES.ACCOUNT_LOCKED);
            } else {
              showToast(ERROR_MESSAGES.FORBIDDEN);
            }
            break;
          case 409:
            // For conflict errors, always use the API message if available as it contains important context
            showToast(errorData?.message || ERROR_MESSAGES.CONFLICT);
            break;
          case 422:
            if (!isSilent) {
              handleValidationError(errorData);
              markToastShown(error);
            }
            break;
          default: {
            const errorMessage = getContextualErrorMessage(status, context);
            showToast(errorMessage);
            break;
          }
        }
      }
    } else if (error.request) {
      showToast(ERROR_MESSAGES.NETWORK_ERROR);
    } else {
      showToast(ERROR_MESSAGES.UNKNOWN_ERROR);
    }

    return Promise.reject(error);
  },
];
